import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Typography, useTheme } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { IconPlus, IconTicket } from '@tabler/icons';
import api from '../../services/api';
import TicketFormDrawer from './TicketFormDrawer';
import TicketDetailDrawer from './TicketDetailDrawer';
import { STATUS_COLORS, STATUS_LABELS } from './ticketUtils';

// Seção "Tickets relacionados" reutilizada em 3 lugares: dentro da
// Conversation (DadosDealDrawer.js), dentro do Deal (DealDrawer.js) e
// dentro do próprio Ticket (TicketDetailDrawer.js, pra mostrar outros
// tickets do mesmo cliente). Não transforma a entidade hospedeira num
// Ticket - só mostra o indicador + atalho pra criar um novo, como pedido.
// Só um filtro é usado por vez (conversation > deal > contact), na
// prioridade do mais específico pro mais genérico.
const RelatedTicketsSection = ({
    appId,
    conversationId,
    dealId,
    contactId,
    organizationId,
    excludeTicketId,
    title = 'Tickets relacionados',
    onCreated
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const history = useHistory();

    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openCreate, setOpenCreate] = useState(false);
    const [openTicketId, setOpenTicketId] = useState(null);

    const filterParam = conversationId
        ? { key: 'conversation', value: conversationId }
        : dealId
        ? { key: 'deal', value: dealId }
        : contactId
        ? { key: 'contact', value: contactId }
        : null;

    const load = async () => {
        if (!appId || !filterParam) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const response = await api.get(`v1/apps/${appId}/tickets/?${filterParam.key}=${filterParam.value}`);
            setTickets((response.data || []).filter((t) => t.id !== excludeTicketId));
        } catch (error) {
            console.error('Erro ao carregar tickets relacionados', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, conversationId, dealId, contactId]);

    if (!filterParam) return null;

    const verTodos = () => {
        history.push(`/dashboard/config/tickets?${filterParam.key}=${filterParam.value}`);
    };

    return (
        <Box>
            <Box
                sx={{
                    p: 1,
                    borderRadius: 2,
                    background: isDark ? '#1f2c33' : '#fff'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography fontWeight={600}>{title}</Typography>
                    <Button size="small" startIcon={<IconPlus size={16} />} onClick={() => setOpenCreate(true)} sx={{ textTransform: 'none' }}>
                        Criar Ticket
                    </Button>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={20} />
                    </Box>
                ) : tickets.length === 0 ? (
                    <Typography variant="body2" sx={{ opacity: 0.6, mt: 1 }}>
                        Nenhum ticket relacionado ainda.
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                        {tickets.slice(0, 5).map((t) => (
                            <Box
                                key={t.id}
                                onClick={() => setOpenTicketId(t.id)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    p: 0.75,
                                    borderRadius: 1.5,
                                    cursor: 'pointer',
                                    '&:hover': { background: isDark ? '#2a3942' : '#f0f0f0' }
                                }}
                            >
                                <IconTicket size={16} style={{ flexShrink: 0, opacity: 0.6 }} />
                                <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {t.title}
                                </Typography>
                                <Chip
                                    label={STATUS_LABELS[t.status] || t.status}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        fontSize: 11,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        bgcolor: STATUS_COLORS[t.status]?.bg,
                                        color: STATUS_COLORS[t.status]?.color
                                    }}
                                />
                            </Box>
                        ))}

                        {tickets.length > 0 && (
                            <Button size="small" onClick={verTodos} sx={{ textTransform: 'none', alignSelf: 'flex-start', mt: 0.5 }}>
                                Ver todos
                            </Button>
                        )}
                    </Box>
                )}
            </Box>

            {openCreate && (
                <TicketFormDrawer
                    appId={appId}
                    initialConversationId={conversationId}
                    initialDealId={dealId}
                    initialContactId={contactId}
                    initialOrganizationId={organizationId}
                    onClose={() => setOpenCreate(false)}
                    onSaved={() => {
                        setOpenCreate(false);
                        load();
                        if (onCreated) onCreated();
                    }}
                />
            )}

            {openTicketId && (
                <TicketDetailDrawer appId={appId} ticketId={openTicketId} onClose={() => setOpenTicketId(null)} onChanged={load} />
            )}
        </Box>
    );
};

export default RelatedTicketsSection;
